
import React, { useState } from 'react';
import { SchoolSettings, PeriodTime } from '../types.ts';
import { Clock, Save, AlertTriangle, CheckCircle } from 'lucide-react';

interface Props {
  settings: SchoolSettings;
  onUpdate: (settings: SchoolSettings) => Promise<any>;
}

const PeriodSettingsView: React.FC<Props> = ({ settings, onUpdate }) => {
  const [formData, setFormData] = useState<SchoolSettings>(settings);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDayForTimes, setSelectedDayForTimes] = useState<string>('Senin');

  const days = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Minggu'];

  React.useEffect(() => {
    setFormData(settings);
  }, [settings]);

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setIsSaving(true);
    setError(null);
    try {
      const result = await onUpdate(formData);
      if (result && (result as any).error) {
        setError((result as any).message || "Gagal menyimpan ke database.");
      } else {
        setIsSaved(true);
        setTimeout(() => setIsSaved(false), 3000);
      }
    } catch (err) {
      console.error("Failed to save settings:", err);
      setError("Terjadi kesalahan jaringan.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in pb-20">
      <div className="bg-white dark:bg-slate-900 p-8 sm:p-10 rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
          <div className="flex-1">
            <h2 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight leading-none mb-3">Pengaturan Jam Pelajaran</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Konfigurasi rentang waktu untuk setiap jam pelajaran per hari.</p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
            {isSaved && (
              <div className="flex items-center gap-2 text-green-600 font-black text-[10px] uppercase tracking-widest animate-fade-in bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-xl border border-green-100 dark:border-green-800">
                <div className="w-2 h-2 bg-green-600 rounded-full animate-ping"></div>
                Berhasil Diperbarui!
              </div>
            )}
            
            <button 
              onClick={() => handleSave()}
              disabled={isSaving}
              className={`flex items-center justify-center gap-3 px-8 py-4 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 shadow-xl shadow-indigo-500/20 active:scale-95 transition-all uppercase tracking-widest text-xs w-full sm:w-auto ${isSaving ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Save size={18} />
              )}
              {isSaving ? 'Menyimpan...' : 'Simpan Perubahan'}
            </button>
          </div>
        </div>

        <form onSubmit={handleSave} className="max-w-6xl space-y-10">
          <div className="space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-indigo-600 text-white rounded-2xl shadow-lg shadow-indigo-500/20">
                  <Clock size={20} />
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Rentang Waktu</h3>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">ATUR RENTANG WAKTU UNTUK SETIAP HARI</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 items-center">
                {days.map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => setSelectedDayForTimes(day)}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                      selectedDayForTimes === day 
                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20' 
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    {day}
                  </button>
                ))}
                
                <div className="flex items-center gap-2 ml-4 p-2 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Salin {selectedDayForTimes} ke:</span>
                  <div className="flex flex-wrap gap-1">
                    {days.filter(d => d !== selectedDayForTimes).map(targetDay => (
                      <button
                        key={targetDay}
                        type="button"
                        onClick={() => {
                          const currentDayTimes = formData.dayPeriodTimes?.[selectedDayForTimes] || formData.periodTimes;
                          if (!currentDayTimes) return;
                          
                          const newDayPeriodTimes = { 
                            ...(formData.dayPeriodTimes || {}),
                            [targetDay]: JSON.parse(JSON.stringify(currentDayTimes))
                          };
                          
                          setFormData({
                            ...formData,
                            dayPeriodTimes: newDayPeriodTimes
                          });
                        }}
                        className="px-2 py-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-[9px] font-black text-indigo-600 uppercase hover:bg-indigo-600 hover:text-white transition-all"
                      >
                        {targetDay}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const currentDayTimes = formData.dayPeriodTimes?.[selectedDayForTimes] || formData.periodTimes;
                        if (!currentDayTimes) return;
                        
                        const newDayPeriodTimes = { ...formData.dayPeriodTimes };
                        days.forEach(day => {
                          newDayPeriodTimes[day] = JSON.parse(JSON.stringify(currentDayTimes));
                        });
                        
                        setFormData({
                          ...formData,
                          dayPeriodTimes: newDayPeriodTimes
                        });
                      }}
                      className="px-2 py-1 bg-emerald-500 text-white rounded-lg text-[9px] font-black uppercase hover:bg-emerald-600 transition-all"
                    >
                      Semua
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {(formData.dayPeriodTimes?.[selectedDayForTimes] || formData.periodTimes || Array.from({ length: 10 }, (_, i) => ({ period: i + 1, startTime: '07:00', endTime: '07:40' }))).map((pt, idx) => (
                <div key={`${selectedDayForTimes}-${pt.period}`} className="p-4 bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700 rounded-2xl space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-black text-indigo-600 uppercase tracking-widest">Jam Ke-{pt.period}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 ml-1">Mulai</label>
                      <input 
                        type="time" 
                        value={pt.startTime}
                        onChange={e => {
                          const currentDayTimes = [...(formData.dayPeriodTimes?.[selectedDayForTimes] || formData.periodTimes || Array.from({ length: 10 }, (_, i) => ({ period: i + 1, startTime: '07:00', endTime: '07:40' })))];
                          currentDayTimes[idx] = { ...pt, startTime: e.target.value };
                          setFormData({ 
                            ...formData, 
                            dayPeriodTimes: { 
                              ...(formData.dayPeriodTimes || {}), 
                              [selectedDayForTimes]: currentDayTimes 
                            } 
                          });
                        }}
                        className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-black outline-none focus:ring-2 focus:ring-indigo-500/20 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 ml-1">Selesai</label>
                      <input 
                        type="time" 
                        value={pt.endTime}
                        onChange={e => {
                          const currentDayTimes = [...(formData.dayPeriodTimes?.[selectedDayForTimes] || formData.periodTimes || Array.from({ length: 10 }, (_, i) => ({ period: i + 1, startTime: '07:00', endTime: '07:40' })))];
                          currentDayTimes[idx] = { ...pt, endTime: e.target.value };
                          setFormData({ 
                            ...formData, 
                            dayPeriodTimes: { 
                              ...(formData.dayPeriodTimes || {}), 
                              [selectedDayForTimes]: currentDayTimes 
                            } 
                          });
                        }}
                        className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-black outline-none focus:ring-2 focus:ring-indigo-500/20 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="p-6 bg-amber-50 dark:bg-amber-500/5 rounded-[2rem] border border-amber-100 dark:border-amber-500/20">
              <p className="text-xs text-amber-700 dark:text-amber-300 font-bold leading-relaxed italic">
                * Anda dapat mengatur waktu yang berbeda untuk setiap hari. Jika hari tertentu belum diatur, sistem akan menggunakan pengaturan default atau pengaturan global.
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PeriodSettingsView;
